import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Package, Ruler, Lock, Trash2, Download, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { generateMaterialQuotePdf } from "@/lib/materialQuotePdf";

function computeMaterialCost(product, surface, thickness) {
  if (product.price_per_usage_unit == null || !surface || surface <= 0) return null;
  let cost = product.price_per_usage_unit * Number(surface);
  const refThk = product.consumption_reference_thickness_mm;
  const reqThk = thickness ? Number(thickness) : null;
  if (refThk && refThk > 0 && reqThk && reqThk > 0) cost = cost * (reqThk / refThk);
  return cost;
}

function computeShippingFee(product, materialCost) {
  if (materialCost == null) return null;
  const threshold = product.shipping_free_threshold;
  const fee = product.shipping_fee;
  if (fee == null || fee <= 0) return 0;
  if (threshold != null && threshold > 0 && materialCost >= threshold) return 0;
  return Number(fee);
}

export const CompatibleProductsDialog = ({ open, onClose, order }) => {
  const [loadingCat, setLoadingCat] = useState(false);
  const [loadingMat, setLoadingMat] = useState(false);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState({
    materials: [], total_materials_cost: 0, amount_ht: 0,
    material_margin: null, material_margin_pct: null, material_cost_pct_of_ht: null,
  });
  const [surface, setSurface] = useState("");
  const [thickness, setThickness] = useState("");
  const [lockingId, setLockingId] = useState(null);
  const [notesById, setNotesById] = useState({});

  const orderId = order?.id;

  const fetchCatalog = useCallback(async () => {
    if (!orderId || !order?.order_type_id) return;
    try {
      setLoadingCat(true);
      const { data } = await api.getAllProducts({ order_type_id: order.order_type_id });
      setProducts(data);
    } catch (_e) {
      toast.error("Erreur chargement catalogue");
    } finally {
      setLoadingCat(false);
    }
  }, [orderId, order?.order_type_id]);

  const fetchMaterials = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoadingMat(true);
      const { data } = await api.getOrderMaterials(orderId);
      setMaterials(data);
    } catch (_e) {
      toast.error("Erreur chargement matières");
    } finally {
      setLoadingMat(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (open) { fetchCatalog(); fetchMaterials(); }
  }, [open, fetchCatalog, fetchMaterials]);

  useEffect(() => {
    if (open) { setSurface(""); setThickness(""); setNotesById({}); }
  }, [open, orderId]);

  const rows = useMemo(() => {
    const withCost = products.map(p => ({ ...p, total_cost: computeMaterialCost(p, surface, thickness) }));
    return withCost.sort((a, b) => {
      const hasSurface = Number(surface) > 0;
      if (hasSurface) {
        const ac = a.total_cost ?? Number.POSITIVE_INFINITY;
        const bc = b.total_cost ?? Number.POSITIVE_INFINITY;
        if (ac !== bc) return ac - bc;
      }
      const au = a.price_per_usage_unit ?? Number.POSITIVE_INFINITY;
      const bu = b.price_per_usage_unit ?? Number.POSITIVE_INFINITY;
      if (au !== bu) return au - bu;
      const ar = a.price_per_reference_unit ?? Number.POSITIVE_INFINITY;
      const br = b.price_per_reference_unit ?? Number.POSITIVE_INFINITY;
      return ar - br;
    });
  }, [products, surface, thickness]);

  const hasEstimation = Number(surface) > 0;

  const handleLock = async (product) => {
    if (!surface || !hasEstimation) {
      toast.error("Veuillez saisir une surface avant de verrouiller.");
      return;
    }
    if (product.total_cost == null) {
      toast.error("Coût non calculable. Vérifiez la consommation et les équivalences.");
      return;
    }
    try {
      setLockingId(product.id);
      await api.lockOrderMaterial(orderId, {
        product_id: product.id,
        surface_m2: parseFloat(surface),
        thickness_mm: thickness ? parseFloat(thickness) : null,
        total_cost: Number(product.total_cost.toFixed(2)),
        notes: notesById[product.id] || null,
      });
      toast.success(`${product.name} verrouillé sur la commande`);
      fetchMaterials();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de verrouillage");
    } finally {
      setLockingId(null);
    }
  };

  const handleDeleteMaterial = async (mat) => {
    if (!window.confirm(`Retirer "${mat.product_name}" de la commande ?`)) return;
    try {
      await api.deleteOrderMaterial(mat.id);
      toast.success("Ligne retirée");
      fetchMaterials();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  const handleExportPdf = () => {
    generateMaterialQuotePdf({
      order,
      surface: surface !== "" ? Number(surface) : null,
      thickness: thickness !== "" ? Number(thickness) : null,
      candidates: rows,
      lockedMaterials: materials.materials || [],
      summary: materials,
    });
    toast.success("Devis matière exporté");
  };

  const marginPct = materials.material_margin_pct;
  const marginColor = marginPct == null ? "bg-muted"
    : marginPct < 20 ? "bg-red-100 text-red-800"
    : marginPct < 40 ? "bg-amber-100 text-amber-800"
    : "bg-green-100 text-green-800";

  // Per-supplier cumul for franco-de-port hints
  const supplierCumuls = useMemo(() => {
    const m = materials.materials || [];
    const groups = new Map();
    for (const row of m) {
      const key = row.supplier_id || row.supplier_name || "unknown";
      if (!groups.has(key)) {
        groups.set(key, {
          supplier_id: row.supplier_id,
          supplier_name: row.supplier_name || "—",
          total: 0,
          threshold: null,
          fee: null,
        });
      }
      const g = groups.get(key);
      g.total += Number(row.total_cost || 0);
      // Use the MAX threshold seen for this supplier — if one product needs 500€ to be franco,
      // we align on the toughest requirement to stay safe.
      if (row.shipping_free_threshold != null && (g.threshold == null || row.shipping_free_threshold > g.threshold)) {
        g.threshold = row.shipping_free_threshold;
      }
      // Use the MAX fee (worst case)
      if (row.shipping_fee != null && (g.fee == null || row.shipping_fee > g.fee)) {
        g.fee = row.shipping_fee;
      }
    }
    return Array.from(groups.values())
      .filter(g => g.threshold != null && g.threshold > 0 && g.fee != null && g.fee > 0)
      .map(g => ({
        ...g,
        missing: Math.max(0, g.threshold - g.total),
        franco_reached: g.total >= g.threshold,
      }));
  }, [materials]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Package className="h-5 w-5 text-primary" />
            Matières & estimation — {order?.reference || order?.client_name}
            <Button size="sm" variant="outline" className="ml-auto" onClick={handleExportPdf} data-testid="export-material-pdf">
              <Download className="h-4 w-4 mr-1" /> Export PDF
            </Button>
          </DialogTitle>
          <DialogDescription>
            Verrouillez un ou plusieurs produits (ex : 2 couches) sur la commande, puis comparez les candidats restants.
          </DialogDescription>
        </DialogHeader>

        {/* Locked materials section */}
        <Card className="mb-2" data-testid="locked-materials-section">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-medium flex items-center gap-2">
                <Lock className="h-4 w-4" /> Matières verrouillées ({materials.materials?.length || 0})
              </h3>
              {materials.amount_ht > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">HT : {formatCurrency(materials.amount_ht)}</Badge>
                  <Badge variant="outline">Matière : {formatCurrency(materials.total_materials_cost || 0)}</Badge>
                  <Badge className={marginColor} data-testid="material-margin-badge">
                    {marginPct != null && (marginPct >= 0 ? <TrendingUp className="h-3 w-3 mr-1 inline" /> : <TrendingDown className="h-3 w-3 mr-1 inline" />)}
                    Marge matière : {materials.material_margin != null ? formatCurrency(materials.material_margin) : "—"}
                    {marginPct != null && ` (${marginPct.toFixed(1)}%)`}
                  </Badge>
                </div>
              )}
            </div>
            {loadingMat ? (
              <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : (materials.materials?.length || 0) === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-4 bg-muted/30 rounded">
                Aucune matière verrouillée. Sélectionnez-en une ci-dessous.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fournisseur</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead>Surface</TableHead>
                    <TableHead>Épaisseur</TableHead>
                    <TableHead className="text-right">Coût</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materials.materials.map((m) => (
                    <TableRow key={m.id} data-testid={`locked-material-${m.id}`}>
                      <TableCell>{m.supplier_name || "—"}</TableCell>
                      <TableCell className="font-medium">
                        {m.product_name}
                        {m.product_reference && <span className="text-xs font-mono text-muted-foreground ml-1">({m.product_reference})</span>}
                      </TableCell>
                      <TableCell>{m.surface_m2} m²</TableCell>
                      <TableCell>{m.thickness_mm != null ? `${m.thickness_mm} mm` : "—"}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(m.total_cost)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{m.notes || ""}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(m)} data-testid={`delete-material-${m.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {supplierCumuls.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="franco-hints-section">
                {supplierCumuls.map((g) => (
                  <div
                    key={g.supplier_id || g.supplier_name}
                    className={`flex items-start gap-2 p-2 rounded border text-sm ${
                      g.franco_reached ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
                    }`}
                    data-testid={`franco-hint-${g.supplier_id || "unknown"}`}
                  >
                    <Package className={`h-4 w-4 mt-0.5 shrink-0 ${g.franco_reached ? "text-green-700" : "text-amber-700"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">
                        Cumul chez <span className="font-semibold">{g.supplier_name}</span> : {formatCurrency(g.total)}
                      </div>
                      {g.franco_reached ? (
                        <div className="text-xs text-green-800">
                          Seuil franco ({formatCurrency(g.threshold)}) atteint — <strong>{formatCurrency(g.fee)} de port économisés</strong>.
                        </div>
                      ) : (
                        <div className="text-xs text-amber-800">
                          Il manque <strong>{formatCurrency(g.missing)}</strong> pour atteindre le franco de port à {formatCurrency(g.threshold)}
                          {" "}— économie possible : <strong>{formatCurrency(g.fee)}</strong>.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Candidates estimation */}
        <Card className="mb-2">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs">Surface (m²) *</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={surface} onChange={(e) => setSurface(e.target.value)}
                  data-testid="estim-surface" placeholder="Ex : 120"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Ruler className="h-3 w-3" /> Épaisseur (mm)
                </Label>
                <Input
                  type="number" step="0.1" min="0"
                  value={thickness} onChange={(e) => setThickness(e.target.value)}
                  data-testid="estim-thickness" placeholder="Ex : 100"
                />
              </div>
              <div className="md:col-span-2 text-sm text-muted-foreground">
                Type de commande : <strong>{order?.order_type_name || "—"}</strong>. Saisissez surface + épaisseur pour chiffrer puis verrouiller.
              </div>
            </div>
          </CardContent>
        </Card>

        {loadingCat ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-8 bg-muted/30 rounded" data-testid="no-compatible-products">
            Aucun produit du catalogue n'est rattaché à « {order?.order_type_name} ». Ajoutez-en depuis la fiche d'un produit.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Fournisseur</TableHead>
                <TableHead>Produit</TableHead>
                <TableHead>Prix / usage</TableHead>
                <TableHead>Ép. réf.</TableHead>
                <TableHead className="text-right">Coût matière</TableHead>
                <TableHead className="text-right">Frais de port</TableHead>
                <TableHead className="text-right">Total livré</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p, idx) => (
                <TableRow key={p.id} data-testid={`compatible-row-${p.id}`} className={idx === 0 && hasEstimation && p.total_delivered_cost != null ? "bg-green-50/40" : ""}>
                  <TableCell>
                    {idx === 0 && hasEstimation && p.total_delivered_cost != null
                      ? <Badge className="bg-green-100 text-green-800">Moins cher</Badge>
                      : idx + 1}
                  </TableCell>
                  <TableCell>{p.supplier_name}</TableCell>
                  <TableCell>
                    <div className="font-medium">{p.name}</div>
                    {p.reference && <div className="text-xs font-mono text-muted-foreground">{p.reference}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-indigo-700">
                    {p.price_per_usage_unit != null && p.consumption_per_unit
                      ? `${formatCurrency(p.price_per_usage_unit)}/${p.consumption_per_unit}`
                      : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.consumption_reference_thickness_mm
                      ? <Badge variant="outline">{p.consumption_reference_thickness_mm} mm</Badge>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {p.total_cost != null ? formatCurrency(p.total_cost)
                      : hasEstimation ? <span className="text-xs text-muted-foreground italic">—</span>
                      : <span className="text-xs text-muted-foreground">saisir surface</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {p.shipping_fee_applied == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : p.shipping_fee_applied === 0 ? (
                      p.shipping_free_threshold && p.total_cost != null && p.total_cost >= p.shipping_free_threshold
                        ? <Badge className="bg-green-100 text-green-800" data-testid={`free-shipping-${p.id}`}>Franco</Badge>
                        : p.shipping_fee ? <Badge className="bg-green-100 text-green-800">Franco</Badge> : <span className="text-muted-foreground">—</span>
                    ) : (
                      <div>
                        <span className="text-amber-700 font-semibold">+ {formatCurrency(p.shipping_fee_applied)}</span>
                        {p.shipping_free_threshold && (
                          <div className="text-xs text-muted-foreground" data-testid={`missing-to-franco-${p.id}`}>
                            franco à {formatCurrency(p.shipping_free_threshold)}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {p.total_delivered_cost != null
                      ? <span className={p.shipping_fee_applied > 0 ? "text-amber-900" : ""}>{formatCurrency(p.total_delivered_cost)}</span>
                      : hasEstimation ? <span className="text-xs text-muted-foreground italic">—</span>
                      : <span className="text-xs text-muted-foreground">saisir surface</span>}
                  </TableCell>
                  <TableCell>
                    <Textarea
                      rows={1} value={notesById[p.id] || ""}
                      onChange={(e) => setNotesById({ ...notesById, [p.id]: e.target.value })}
                      placeholder="Ex : couche 1, finition..."
                      className="h-8 min-h-0 text-xs"
                      data-testid={`lock-notes-${p.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm" variant="default"
                      onClick={() => handleLock(p)}
                      disabled={lockingId === p.id || !hasEstimation || p.total_cost == null}
                      data-testid={`lock-product-${p.id}`}
                      title={hasEstimation ? "Verrouiller ce produit sur la commande" : "Saisissez une surface"}
                    >
                      {lockingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompatibleProductsDialog;
